import React from "react";
import classNames from "classnames";
import { LoadingSpinner } from "App/common/icons/LoadingSpinner";
import styles from "./ButtonV2.module.scss";

export type ButtonV2Color =
  | "primary"
  | "secondary"
  | "ghost"
  | "flat-black"
  | "flat-white"
  | "danger"
  | "monetization"
  | "monetization-inverse";

export type ButtonV2Variant = "solid" | "outlined" | "text" | "plain";

export type ButtonV2Size = "xs" | "sm" | "md" | "lg";

export type ButtonV2Shape = "rounded" | "circle";

export interface ButtonV2Props
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "color" | "theme"
  > {
  asChild?: boolean;
  color?: ButtonV2Color;
  variant?: ButtonV2Variant;
  size?: ButtonV2Size;
  shape?: ButtonV2Shape;
  width?: "width-auto" | "width-full" | "width-fit" | "width-max" | "width-min";
  isLoading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export const ButtonV2 = React.forwardRef<HTMLButtonElement, ButtonV2Props>(
  (
    {
      asChild,
      className,
      color = "secondary",
      variant = "solid",
      size = "md",
      shape = "rounded",
      width = "width-auto",
      startIcon,
      endIcon,
      isLoading,
      children,
      ...props
    },
    ref,
  ) => {
    if (asChild && React.isValidElement(children)) {
      const childProps = {
        className: classNames(
          styles.button,
          styles[width],
          styles[color],
          styles[variant],
          styles[size],
          styles[shape],
          className,
          children.props.className,
        ),
        ref,
        children: (
          <>
            {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
            {children}
            {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
            {isLoading && (
              <span className={styles.loading}>
                <LoadingSpinner />
              </span>
            )}
          </>
        ),
        ...props,
      };

      // We use cloneElement here to inject props and additional elements (startIcon, endIcon, isLoading, etc.)
      // into the child. Radix UI's <Slot> only forwards props to a single child and does not allow
      // injecting or rearranging content inside the child element.
      return React.cloneElement(children, childProps);
    }

    return (
      <button
        className={classNames(
          styles.button,
          styles[width],
          styles[color],
          styles[variant],
          styles[size],
          styles[shape],
          className,
        )}
        ref={ref}
        {...props}
      >
        {startIcon && <span className={styles.startIcon}>{startIcon}</span>}
        {children}
        {endIcon && <span className={styles.endIcon}>{endIcon}</span>}
        {isLoading && (
          <span className={styles.loading}>
            <LoadingSpinner />
          </span>
        )}
      </button>
    );
  },
);

ButtonV2.displayName = "ButtonV2";
